import { ArrowDownCircle, CheckCircle2, CircleDashed, CircleOff, Navigation, RefreshCcw } from 'lucide-react';

import { Pill, PillProps } from '@/components/ui/pill';
import { SHIPMENT_STATUS, SHIPMENT_SUB_STATUS } from '@/lib/constants';
import { TRACKING_STATUS } from '@/lib/models/tracking-request/types';
import { ShipmentStatus, ShipmentTrackingStatus } from '@/types';
import { tv } from '@/utils/styles';

export interface ShipmentStatusIndicatorProps {
  status?: ShipmentStatus | ShipmentTrackingStatus;
  subStatus?: string;
  customLabel?: string;
  compact?: boolean;
}

const getStatusIconColorStyle = (status?: ShipmentStatus | ShipmentTrackingStatus): string => {
  switch (status) {
    case SHIPMENT_STATUS.COMPLETED:
    case SHIPMENT_STATUS.DELIVERED:
    case TRACKING_STATUS.RESOLVED:
      return 'text-green-500';
    case SHIPMENT_STATUS.UNTRACKABLE:
    case TRACKING_STATUS.UNTRACKABLE:
      return 'text-red-300';
    case SHIPMENT_STATUS.ACTIVE:
      return 'text-lightBlue-500';
    default:
      return 'text-grey-500';
  }
};

const getShipmentStatusConfig = (
  status?: ShipmentStatus | ShipmentTrackingStatus,
  subStatus?: string,
): { label: string; variant: PillProps['variant']; icon: React.ReactNode; iconClassName: string } => {
  const colorStyle = getStatusIconColorStyle(status);

  if (status === SHIPMENT_STATUS.COMPLETED)
    return { label: 'Completed', variant: 'success', icon: <CheckCircle2 />, iconClassName: colorStyle };

  if (status === TRACKING_STATUS.RESOLVED)
    return { label: 'Tracked', variant: 'success', icon: <CheckCircle2 />, iconClassName: colorStyle };

  if (status === SHIPMENT_STATUS.DELIVERED)
    return { label: 'Delivered', variant: 'success', icon: <CheckCircle2 />, iconClassName: colorStyle };

  if (status === SHIPMENT_STATUS.NEW)
    return { label: 'New', variant: 'default', icon: <CircleDashed />, iconClassName: colorStyle };

  if (status === SHIPMENT_STATUS.UNTRACKABLE)
    return { label: 'Not trackable', variant: 'default', icon: <CircleOff />, iconClassName: colorStyle };

  if (status === SHIPMENT_STATUS.IN_PROGRESS)
    return { label: 'In Progress', variant: 'info', icon: <CircleDashed />, iconClassName: colorStyle };

  if (subStatus) {
    if (subStatus === SHIPMENT_SUB_STATUS.DESTINATION) {
      return { label: subStatus, variant: 'info', icon: <CheckCircle2 />, iconClassName: colorStyle };
    }

    if (subStatus === SHIPMENT_SUB_STATUS.DESTINATION_PORT) {
      return { label: 'Discharged', variant: 'info', icon: <ArrowDownCircle />, iconClassName: colorStyle };
    }

    if (subStatus === SHIPMENT_SUB_STATUS.IN_TRANSIT) {
      return { label: subStatus, variant: 'info', icon: <Navigation />, iconClassName: colorStyle };
    }

    if (subStatus === SHIPMENT_SUB_STATUS.TRANSHIPMENT_PORT) {
      return { label: subStatus, variant: 'info', icon: <RefreshCcw />, iconClassName: colorStyle };
    }

    // Handle remaining sub status
    return { label: subStatus, variant: 'info', icon: <CircleDashed />, iconClassName: colorStyle };
  }

  return { label: 'Pending', variant: 'default', icon: <CircleDashed />, iconClassName: colorStyle };
};

const ShipmentStatusIndicator = ({ status, subStatus, customLabel, compact }: ShipmentStatusIndicatorProps) => {
  const statusConfig = getShipmentStatusConfig(status, subStatus);

  const { label, icon } = styles();

  return compact ? (
    <div title={customLabel || statusConfig.label} className="flex max-w-full items-center gap-1">
      <div className={icon({ className: statusConfig.iconClassName })}>{statusConfig.icon}</div>
      <div className={label()}>{customLabel || statusConfig.label}</div>
    </div>
  ) : (
    <Pill
      size="compact"
      iconLeft={statusConfig.icon}
      label={customLabel || statusConfig.label}
      variant={statusConfig.variant}
      className="!inline-flex max-w-full"
      caseSensitive
    />
  );
};

export const styles = tv({
  slots: {
    label: 'whitespace-nowrap text-sm uppercase',
    icon: 'flex items-center [&_svg]:size-4',
  },
});

export { getShipmentStatusConfig, ShipmentStatusIndicator };
